// client/src/components/admin/SeoSettings.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Box, Button, Checkbox, Chip, FormControlLabel,
  IconButton, LinearProgress, Link, Paper, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { jwtDecode } from 'jwt-decode';

const EMPTY_SETTINGS = {
  'seo.siteUrl': '',
  'seo.metaDescription': '',
  'seo.aboutHeadline': 'Track Everything You Collect',
  'seo.aboutTagline': 'One place for all your collections',
  'seo.aboutDescription': '',
  'seo.aboutFeatures': '[]',
  'seo.aboutCtaText': 'Get Started Free',
  'seo.googleAnalyticsId': '',
  'seo.checklist.googleSubmitted': false,
  'seo.checklist.ownershipVerified': false,
  'seo.checklist.sitemapSubmitted': false,
};

export default function SeoSettings() {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;

  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [features, setFeatures] = useState([]);
  const [saving, setSaving] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const metaDescLen = (settings['seo.metaDescription'] || '').length;

  const loadSettings = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/seo-settings');
      setSettings(res.data);
      try { setFeatures(JSON.parse(res.data['seo.aboutFeatures'] || '[]')); } catch (_) { setFeatures([]); }
    } catch (_) {}
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const save = async (section, extraKeys = {}) => {
    setSaving(section);
    try {
      const payload = { ...extraKeys };
      if (section === 'A') {
        payload['seo.siteUrl'] = settings['seo.siteUrl'];
        payload['seo.metaDescription'] = settings['seo.metaDescription'];
      } else if (section === 'B') {
        payload['seo.aboutHeadline'] = settings['seo.aboutHeadline'];
        payload['seo.aboutTagline'] = settings['seo.aboutTagline'];
        payload['seo.aboutDescription'] = settings['seo.aboutDescription'];
        payload['seo.aboutCtaText'] = settings['seo.aboutCtaText'];
        payload['seo.aboutFeatures'] = JSON.stringify(features);
      } else if (section === 'C') {
        payload['seo.googleAnalyticsId'] = settings['seo.googleAnalyticsId'];
      }
      await axiosInstance.put('/admin/seo-settings', payload);
      setSnack({ open: true, message: 'Saved', severity: 'success' });
    } catch (_) {
      setSnack({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setSaving(null);
    }
  };

  const handleChecklistChange = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    try {
      await axiosInstance.put('/admin/seo-settings', { [key]: value });
    } catch (_) {}
  };

  const addFeatureRow = () => setFeatures(prev => [...prev, { title: '', description: '' }]);
  const removeFeatureRow = i => setFeatures(prev => prev.filter((_, idx) => idx !== i));
  const moveFeatureRow = (i, dir) => {
    setFeatures(prev => {
      const arr = [...prev];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const updateFeatureRow = (i, field, value) => {
    setFeatures(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };

  const sitemapUrl = `${settings['seo.siteUrl'] || window.location.origin}/sitemap.xml`;

  const steps = [
    { label: 'Sitemap live at /sitemap.xml', auto: true, done: featureFlags.enableDiscovery },
    { label: 'robots.txt references sitemap', auto: true, done: true },
    { label: 'Submit site to Google Search Console', auto: false, key: 'seo.checklist.googleSubmitted' },
    { label: 'Verify site ownership', auto: false, key: 'seo.checklist.ownershipVerified' },
    { label: 'Submit sitemap URL to Google', auto: false, key: 'seo.checklist.sitemapSubmitted' },
  ];
  const completedCount = steps.filter(s => s.auto ? s.done : settings[s.key]).length;

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  return (
    <>
      <AppBarHeader isAdmin={user?.isAdmin} userId={user?.id} onLogout={handleLogout} />
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/admin')}>
          ← Back to Admin Dashboard
        </Button>
        <Typography variant="h5" gutterBottom>SEO & Discovery</Typography>

        {/* Section A — Site Identity */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">A — Site Identity</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth label="Site URL" sx={{ mb: 2 }}
              value={settings['seo.siteUrl']}
              onChange={e => setSettings(p => ({ ...p, 'seo.siteUrl': e.target.value }))}
              helperText="Used in sitemap and robots.txt — must match your public domain (e.g. https://intercollect.app)"
            />
            <TextField
              fullWidth label="Meta Description" multiline rows={2} sx={{ mb: 2 }}
              value={settings['seo.metaDescription']}
              onChange={e => setSettings(p => ({ ...p, 'seo.metaDescription': e.target.value }))}
              error={metaDescLen > 160}
              helperText={`${metaDescLen}/160 characters${metaDescLen > 160 ? ' — too long' : ''}`}
            />
            <Button variant="contained" onClick={() => save('A')} disabled={saving === 'A'}>
              {saving === 'A' ? 'Saving…' : 'Save'}
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Section B — About Page */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">B — "What is InterCollect" Page</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!featureFlags.enableDiscovery ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Enable the <strong>Discovery</strong> feature flag to unlock this section.
              </Alert>
            ) : (
              <>
                <TextField fullWidth label="Headline" sx={{ mb: 2 }}
                  value={settings['seo.aboutHeadline']}
                  onChange={e => setSettings(p => ({ ...p, 'seo.aboutHeadline': e.target.value }))} />
                <TextField fullWidth label="Tagline" sx={{ mb: 2 }}
                  value={settings['seo.aboutTagline']}
                  onChange={e => setSettings(p => ({ ...p, 'seo.aboutTagline': e.target.value }))} />
                <TextField fullWidth label="Description" multiline rows={4} sx={{ mb: 2 }}
                  value={settings['seo.aboutDescription']}
                  onChange={e => setSettings(p => ({ ...p, 'seo.aboutDescription': e.target.value }))} />
                <TextField fullWidth label="CTA Button Text" sx={{ mb: 2 }}
                  value={settings['seo.aboutCtaText']}
                  onChange={e => setSettings(p => ({ ...p, 'seo.aboutCtaText': e.target.value }))} />

                <Typography variant="subtitle2" gutterBottom>Feature Cards</Typography>
                <Table size="small" sx={{ mb: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell width={120}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {features.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <TextField size="small" fullWidth value={row.title}
                            onChange={e => updateFeatureRow(i, 'title', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={row.description}
                            onChange={e => updateFeatureRow(i, 'description', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Move up">
                            <IconButton size="small" onClick={() => moveFeatureRow(i, -1)}>
                              <KeyboardArrowUpIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <IconButton size="small" onClick={() => moveFeatureRow(i, 1)}>
                              <KeyboardArrowDownIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" onClick={() => removeFeatureRow(i)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="small" startIcon={<AddIcon />} onClick={addFeatureRow} sx={{ mb: 2 }}>
                  Add Row
                </Button>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={() => save('B')} disabled={saving === 'B'}>
                    {saving === 'B' ? 'Saving…' : 'Save'}
                  </Button>
                  <Button variant="outlined" onClick={() => window.open('/about', '_blank')}>
                    Preview /about page ↗
                  </Button>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Section C — Google Analytics */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">C — Google Analytics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth label="GA4 Measurement ID" sx={{ mb: 1 }}
              placeholder="G-XXXXXXXXXX"
              value={settings['seo.googleAnalyticsId']}
              onChange={e => setSettings(p => ({ ...p, 'seo.googleAnalyticsId': e.target.value }))}
              helperText="Find this in Google Analytics → Admin → Data Streams → your stream → Measurement ID"
            />
            <Chip label="Do Not Track browser preference is always honoured" size="small" sx={{ mb: 2 }} />
            <Box>
              <Button variant="contained" onClick={() => save('C')} disabled={saving === 'C'}>
                {saving === 'C' ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Checklist */}
        <Paper sx={{ mt: 3, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">Search Engine Setup</Typography>
            <Typography variant="body2" color="text.secondary">
              {completedCount} / {steps.length} steps complete
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={(completedCount / steps.length) * 100} sx={{ mb: 2 }} />
          {steps.map((step, i) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {step.auto ? (
                  <Typography sx={{ minWidth: 24 }}>{step.done ? '✅' : '☐'}</Typography>
                ) : (
                  <Checkbox
                    size="small"
                    checked={!!settings[step.key]}
                    onChange={e => handleChecklistChange(step.key, e.target.checked)}
                    sx={{ p: 0 }}
                  />
                )}
                <Typography variant="body2">
                  {i + 1}. {step.label}
                </Typography>
                <Chip label={step.auto ? 'auto' : 'manual'} size="small" variant="outlined" />
              </Box>
              {i === 2 && (
                <Box sx={{ ml: 4 }}>
                  <Link href="https://search.google.com/search-console" target="_blank" variant="body2">
                    Open Search Console ↗
                  </Link>
                </Box>
              )}
              {i === 4 && (
                <Box sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">{sitemapUrl}</Typography>
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(sitemapUrl)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      </Box>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
}
